import { useState, useMemo } from 'react';

import { useLoansStore, useRevenueStore, useCustomersStore } from '@/hooks/useSupabaseStore';
import { useCapital } from '@/hooks/useCapital';

type Period = 'week' | 'month' | 'quarter' | 'year';

function getPeriodLabel(period: Period) {
  const now = new Date();
  if (period === 'week') {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return `${start.toLocaleDateString('vi-VN')} – ${now.toLocaleDateString('vi-VN')}`;
  }
  if (period === 'month') return `Tháng ${now.getMonth() + 1}/${now.getFullYear()}`;
  if (period === 'quarter') {
    const q = Math.floor(now.getMonth() / 3) + 1;
    return `Quý ${q}/${now.getFullYear()}`;
  }
  return `Năm ${now.getFullYear()}`;
}

function isInPeriod(dateStr: string, period: Period): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  if (period === 'week') {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    return date >= start;
  }
  if (period === 'month') {
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }
  if (period === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    const dq = Math.floor(date.getMonth() / 3);
    return dq === q && date.getFullYear() === now.getFullYear();
  }
  return date.getFullYear() === now.getFullYear();
}

export default function ReportsPage() {
  const { loans, loading: loansLoading } = useLoansStore();
  const { logs, loading: revenueLoading } = useRevenueStore();
  const { customers, loading: customersLoading } = useCustomersStore();
  const [period, setPeriod] = useState<Period>('month');

  const loading = loansLoading || revenueLoading || customersLoading;

  const { totalCapital, capitalLogs } = useCapital();
  const totalActiveLoanAmount = loans
    .filter(l => l.status !== 'redeemed' && l.status !== 'forfeited')
    .reduce((s, l) => s + l.loanAmount, 0);
  const remainingCapital = Math.max(0, totalCapital - totalActiveLoanAmount);

  const stats = useMemo(() => {
    const periodLogs = logs.filter(l => isInPeriod(l.createdAt, period));
    const periodLoans = loans.filter(l => isInPeriod(l.startDate, period));

    const totalRevenue = periodLogs.reduce((s, l) => s + l.interestAmount, 0);
    const totalLoansCreated = periodLoans.length;
    const totalLoanAmount = periodLoans.reduce((s, l) => s + l.loanAmount, 0);
    const redeemedCount = periodLogs.filter(l => l.type === 'redeem').length;
    const extendCount = periodLogs.filter(l => l.type === 'extend').length;

    // Doanh thu theo loại
    const redeemRevenue = periodLogs.filter(l => l.type === 'redeem').reduce((s, l) => s + l.interestAmount, 0);
    const extendRevenue = periodLogs.filter(l => l.type === 'extend').reduce((s, l) => s + l.interestAmount, 0);

    // Khoản vay theo danh mục
    const byCategory: Record<string, { count: number; amount: number }> = {};
    periodLoans.forEach(l => {
      if (!byCategory[l.assetCategory]) byCategory[l.assetCategory] = { count: 0, amount: 0 };
      byCategory[l.assetCategory].count++;
      byCategory[l.assetCategory].amount += l.loanAmount;
    });
    const categoryList = Object.entries(byCategory)
      .map(([cat, v]) => ({ cat, ...v }))
      .sort((a, b) => b.amount - a.amount);

    // Top khách hàng theo số tiền vay trong kỳ
    const customerMap: Record<string, { name: string; phone: string; amount: number; count: number }> = {};
    periodLoans.forEach(l => {
      if (!customerMap[l.customerId]) customerMap[l.customerId] = { name: l.customerName, phone: l.customerPhone, amount: 0, count: 0 };
      customerMap[l.customerId].amount += l.loanAmount;
      customerMap[l.customerId].count++;
    });
    const topCustomers = Object.values(customerMap).sort((a, b) => b.amount - a.amount).slice(0, 5);

    // Trạng thái khoản vay hiện tại (toàn bộ)
    const statusCount = {
      active: loans.filter(l => l.status === 'active').length,
      overdue: loans.filter(l => l.status === 'overdue').length,
      due_soon: loans.filter(l => l.status === 'due_soon').length,
      redeemed: loans.filter(l => l.status === 'redeemed').length,
      forfeited: loans.filter(l => l.status === 'forfeited').length,
    };

    // Lịch sử thay đổi vốn trong kỳ
    const periodCapitalLogs = capitalLogs.filter(l => isInPeriod(l.createdAt, period));
    const totalDeposit = periodCapitalLogs.filter(l => l.type === 'deposit').reduce((s, l) => s + l.amount, 0);
    const totalWithdraw = periodCapitalLogs.filter(l => l.type === 'withdraw').reduce((s, l) => s + l.amount, 0);

    return {
      totalRevenue, totalLoansCreated, totalLoanAmount,
      redeemedCount, extendCount, redeemRevenue, extendRevenue,
      categoryList, topCustomers, statusCount,
      totalCustomers: customers.length,
      activeLoans: loans.filter(l => ['active', 'overdue', 'due_soon'].includes(l.status)).length,
      totalDeposit, totalWithdraw, periodCapitalLogs,
    };
  }, [logs, loans, customers, period, capitalLogs]);

  const fmt = (n: number) => n.toLocaleString('vi-VN');

  const periods: { key: Period; label: string }[] = [
    { key: 'week', label: '7 ngày' },
    { key: 'month', label: 'Tháng này' },
    { key: 'quarter', label: 'Quý này' },
    { key: 'year', label: 'Năm này' },
  ];

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center py-32 text-gray-400">
          <i className="ri-loader-4-line animate-spin text-3xl mr-3"></i>
          <span className="text-sm">Đang tải dữ liệu...</span>
        </div>
      </>
    );
  }

  return (<>
      {/* Period selector */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-xs text-gray-400 mt-0.5">{getPeriodLabel(period)}</p>
        </div>
        <div className="flex items-center gap-1 bg-white rounded-xl p-1">
          {periods.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${period === p.key ? 'bg-[#f59e0b] text-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Doanh thu lãi', value: `${fmt(stats.totalRevenue)}đ`, icon: 'ri-money-dollar-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Khoản vay mới', value: stats.totalLoansCreated, icon: 'ri-file-add-line', color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Tổng vốn cho vay', value: `${fmt(stats.totalLoanAmount)}đ`, icon: 'ri-bank-line', color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Lượt chuộc đồ', value: stats.redeemedCount, icon: 'ri-checkbox-circle-line', color: 'text-teal-600', bg: 'bg-teal-50' },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-xs">{card.label}</p>
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${card.bg}`}>
                <i className={`${card.icon} ${card.color} text-base`}></i>
              </div>
            </div>
            <p className={`font-bold text-lg md:text-xl ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Quỹ vốn kinh doanh */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-gray-400 text-xs">Tổng vốn kinh doanh</p>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50">
              <i className="ri-safe-2-line text-gray-600 text-base"></i>
            </div>
          </div>
          <p className="font-bold text-lg md:text-xl text-gray-700">{fmt(totalCapital)}đ</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-gray-400 text-xs">Vốn còn lại</p>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50">
              <i className="ri-wallet-3-line text-emerald-600 text-base"></i>
            </div>
          </div>
          <p className={`font-bold text-lg md:text-xl ${remainingCapital > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(remainingCapital)}đ</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-gray-400 text-xs">Tỷ lệ vốn đã dùng</p>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
              <i className="ri-percent-line text-amber-600 text-base"></i>
            </div>
          </div>
          <p className="font-bold text-lg md:text-xl text-amber-600">
            {totalCapital > 0 ? Math.round(((totalCapital - remainingCapital) / totalCapital) * 100) : 0}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Doanh thu theo loại */}
        <div className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">Doanh thu theo loại</h3>
          <div className="space-y-3">
            {[
              { label: 'Lãi chuộc đồ', value: stats.redeemRevenue, color: 'bg-teal-400', total: stats.totalRevenue },
              { label: 'Lãi gia hạn', value: stats.extendRevenue, color: 'bg-amber-400', total: stats.totalRevenue },
            ].map(item => {
              const pct = stats.totalRevenue > 0 ? Math.round((item.value / item.total) * 100) : 0;
              return (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">{item.label}</span>
                    <span className="text-xs font-semibold text-gray-700">{fmt(item.value)}đ <span className="text-gray-400 font-normal">({pct}%)</span></span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${pct}%` }}></div>
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t border-gray-100 flex justify-between">
              <span className="text-xs text-gray-400">Tổng lãi kỳ này</span>
              <span className="text-sm font-bold text-emerald-600">{fmt(stats.totalRevenue)}đ</span>
            </div>
          </div>
        </div>

        {/* Tài sản theo danh mục */}
        <div className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">Tài sản theo danh mục</h3>
          {stats.categoryList.length === 0 ? (
            <p className="text-gray-400 text-xs text-center py-6">Không có dữ liệu trong kỳ này</p>
          ) : (
            <div className="space-y-2.5">
              {stats.categoryList.map((c, i) => {
                const maxAmt = stats.categoryList[0]?.amount || 1;
                const pct = Math.round((c.amount / maxAmt) * 100);
                const colors = ['bg-amber-400', 'bg-teal-400', 'bg-blue-400', 'bg-purple-400', 'bg-rose-400', 'bg-gray-400'];
                return (
                  <div key={c.cat}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600 font-medium">{c.cat}</span>
                      <span className="text-xs text-gray-400">{c.count} khoản · {fmt(c.amount)}đ</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Trạng thái khoản vay hiện tại */}
        <div className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">Trạng thái khoản vay <span className="text-gray-400 font-normal">(hiện tại)</span></h3>
          <div className="space-y-2.5">
            {[
              { label: 'Đang vay', value: stats.statusCount.active, color: 'bg-emerald-400', textColor: 'text-emerald-600' },
              { label: 'Sắp đến hạn', value: stats.statusCount.due_soon, color: 'bg-amber-400', textColor: 'text-amber-600' },
              { label: 'Quá hạn', value: stats.statusCount.overdue, color: 'bg-red-400', textColor: 'text-red-600' },
              { label: 'Đã chuộc', value: stats.statusCount.redeemed, color: 'bg-gray-300', textColor: 'text-gray-500' },
              { label: 'Tịch thu', value: stats.statusCount.forfeited, color: 'bg-purple-400', textColor: 'text-purple-600' },
            ].map(s => {
              const total = loans.length || 1;
              const pct = Math.round((s.value / total) * 100);
              return (
                <div key={s.label} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{}} >
                    <div className={`w-2 h-2 rounded-full ${s.color}`}></div>
                  </div>
                  <span className="text-xs text-gray-500 flex-1">{s.label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${s.color} rounded-full`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className={`text-xs font-semibold w-6 text-right ${s.textColor}`}>{s.value}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-800">{stats.activeLoans}</p>
              <p className="text-xs text-gray-400">Đang hoạt động</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-800">{stats.totalCustomers}</p>
              <p className="text-xs text-gray-400">Tổng khách hàng</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top khách hàng + Giao dịch gần đây + Lịch sử vốn */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top khách hàng */}
        <div className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">Top khách hàng <span className="text-gray-400 font-normal text-xs">(theo vốn vay trong kỳ)</span></h3>
          {stats.topCustomers.length === 0 ? (
            <p className="text-gray-400 text-xs text-center py-6">Không có dữ liệu trong kỳ này</p>
          ) : (
            <div className="space-y-3">
              {stats.topCustomers.map((c, i) => (
                <div key={c.name + i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <span className="text-amber-700 font-bold text-xs">{c.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.phone} · {c.count} khoản</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-700 whitespace-nowrap">{fmt(c.amount)}đ</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Giao dịch gần đây */}
        <div className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">Giao dịch gần đây</h3>
          {logs.length === 0 ? (
            <p className="text-gray-400 text-xs text-center py-6">Chưa có giao dịch nào</p>
          ) : (
            <div className="space-y-3">
              {logs.slice(0, 6).map(log => (
                <div key={log.id} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${log.type === 'redeem' ? 'bg-teal-100' : 'bg-amber-100'}`}>
                    <i className={`text-xs ${log.type === 'redeem' ? 'ri-checkbox-circle-line text-teal-600' : 'ri-time-line text-amber-600'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{log.customerName} · {log.assetName}</p>
                    <p className="text-xs text-gray-400">{new Date(log.createdAt).toLocaleDateString('vi-VN')} · {log.type === 'redeem' ? 'Chuộc đồ' : 'Gia hạn'}</p>
                  </div>
                  <p className={`text-xs font-semibold whitespace-nowrap ${log.interestAmount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {log.interestAmount >= 0 ? '+' : ''}{fmt(log.interestAmount)}đ
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lịch sử thay đổi vốn */}
        <div className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">Biến động vốn <span className="text-gray-400 font-normal text-xs">(trong kỳ)</span></h3>
          <div className="flex items-center gap-4 mb-4 pb-3 border-b border-gray-100">
            <div className="flex-1 text-center">
              <p className="text-xs text-gray-400 mb-0.5">Nạp thêm</p>
              <p className="text-sm font-bold text-emerald-600">+{fmt(stats.totalDeposit)}đ</p>
            </div>
            <div className="w-px h-8 bg-gray-100"></div>
            <div className="flex-1 text-center">
              <p className="text-xs text-gray-400 mb-0.5">Rút vốn</p>
              <p className="text-sm font-bold text-red-500">-{fmt(stats.totalWithdraw)}đ</p>
            </div>
            <div className="w-px h-8 bg-gray-100"></div>
            <div className="flex-1 text-center">
              <p className="text-xs text-gray-400 mb-0.5">Chênh lệch</p>
              <p className={`text-sm font-bold ${stats.totalDeposit - stats.totalWithdraw >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {stats.totalDeposit - stats.totalWithdraw >= 0 ? '+' : ''}{fmt(stats.totalDeposit - stats.totalWithdraw)}đ
              </p>
            </div>
          </div>
          {stats.periodCapitalLogs.length === 0 ? (
            <p className="text-gray-400 text-xs text-center py-4">Không có giao dịch vốn trong kỳ này</p>
          ) : (
            <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
              {stats.periodCapitalLogs.map(log => (
                <div key={log.id} className="flex items-center gap-3">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${log.type === 'deposit' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-500'}`}>
                    <i className={`text-xs ${log.type === 'deposit' ? 'ri-add-line' : 'ri-subtract-line'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">
                      {log.type === 'deposit' ? 'Nhập thêm vốn' : 'Rút vốn'} · {fmt(log.amount)}đ
                    </p>
                    {log.note && <p className="text-xs text-gray-400 truncate">{log.note}</p>}
                  </div>
                  <p className="text-xs text-gray-400 shrink-0">{new Date(log.createdAt).toLocaleDateString('vi-VN')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
